/**
 * Procedural audio. No asset files: every sound is synthesised from noise and
 * oscillators when it plays — zero download, zero licensing. The AudioContext
 * is created lazily and resumed from the pointer-lock click; until then every
 * method is a no-op, as is the whole class under node. Every sound that happens
 * somewhere in the world is placed via `setListener` + a `StereoPannerNode`
 * (`panFor` in `pan.ts`); sounds that happen to the PLAYER stay centred.
 */

import type { MaterialId, RaidEnv, Vec3 } from "@rietz/shared";
import { panFor } from "./pan.js";

/**
 * Everything is mixed under this. The volume slider's 50 maps to exactly this
 * value (`masterGainFor`).
 */
const MASTER_GAIN = 0.5;

/** The volume slider's unity point. 50 is `MASTER_GAIN`, 100 is twice it. */
const VOLUME_UNITY = 50;

/** Ceiling on the master gain — past ~1.2 the loudest events clip. */
const MASTER_GAIN_MAX = 1.2;

/** Slider position 0..100 → the master node's gain. */
function masterGainFor(volume: number): number {
  if (!Number.isFinite(volume)) return MASTER_GAIN;
  const g = MASTER_GAIN * (volume / VOLUME_UNITY);
  return g < 0 ? 0 : g > MASTER_GAIN_MAX ? MASTER_GAIN_MAX : g;
}

/** Length of the reusable white-noise buffer, seconds. */
const NOISE_SECONDS = 0.5;

/** Remote gunshots fade linearly to silence at this range. */
const SHOT_AUDIBLE_RANGE_M = 150;

/**
 * Bullet impacts fade to silence here — far shorter than a gunshot's 150 m, or
 * every firefight is a wall of clicks.
 */
const IMPACT_AUDIBLE_RANGE_M = 80;

/**
 * Explosions fade to silence here — nearly three times a gunshot's; hearing one
 * two streets away is information worth having.
 */
const EXPLOSION_AUDIBLE_RANGE_M = 400;

/**
 * Inside this an explosion also leaves the ears ringing — the same ring a
 * flashbang produces.
 */
const EXPLOSION_RING_RANGE_M = 5;

/**
 * Metres between footsteps — distance-driven like `character.ts`'s gait, so
 * steps land with the legs at any speed.
 */
const STEP_DISTANCE_M = 2.1;

/**
 * The four knobs a gunshot is made of, per weapon. `bandHz` is the character: a
 * 9 mm snaps high, a 12-gauge booms low.
 */
interface ShotFlavor {
  gain: number;
  bandHz: number;
  /** Stretches the tail. >1 is a bigger, slower-decaying report. */
  decay: number;
  thumpHz: number;
  thumpGain: number;
  thumpSec: number;
}

/** The Phase-3 sound, verbatim — and the fallback for any unknown id. */
const CARBINE_SHOT: ShotFlavor = {
  gain: 0.9,
  bandHz: 2600,
  decay: 1,
  thumpHz: 78,
  thumpGain: 0.6,
  thumpSec: 0.16,
};

/** Keyed by item def id. */
const SHOT_FLAVORS: Record<string, ShotFlavor> = {
  carbine: CARBINE_SHOT,
  // Small, fast and bright: it has to survive being fired ten times a second
  // without turning into mud, so the tail is the shortest in the roster.
  smg: { gain: 0.68, bandHz: 3300, decay: 0.6, thumpHz: 95, thumpGain: 0.34, thumpSec: 0.09 },
  // All bottom end and no crack — a 12-gauge is felt more than it is heard.
  shotgun: { gain: 1, bandHz: 1400, decay: 1.55, thumpHz: 54, thumpGain: 0.9, thumpSec: 0.28 },
  // Loud and long: the one shot in the game everybody in the valley hears.
  dmr: { gain: 1, bandHz: 2100, decay: 1.4, thumpHz: 66, thumpGain: 0.75, thumpSec: 0.22 },
  // The one everybody hears twice: a fraction deeper, a longer tail, the
  // biggest thump after the shotgun.
  sniper: { gain: 1, bandHz: 1950, decay: 1.7, thumpHz: 60, thumpGain: 0.82, thumpSec: 0.26 },
  // The same cartridge as the MP 9 out of a shorter barrel: a flatter crack
  // with a touch more body behind it, and nothing like the rifles' tail.
  pistol: { gain: 0.72, bandHz: 3000, decay: 0.7, thumpHz: 100, thumpGain: 0.38, thumpSec: 0.1 },
};

/**
 * What a can does to a report, as a transform on the flavour: it takes the
 * crack out and leaves the mechanism (band drops by more than half, tail
 * shortens, thump barely moves). Local audio only — other people's suppressors
 * are not a fact this client is told.
 */
const SUPPRESSED_GAIN = 0.4;
const SUPPRESSED_BAND = 0.42;
const SUPPRESSED_DECAY = 0.7;
const SUPPRESSED_THUMP_GAIN = 0.55;

function suppress(f: ShotFlavor): ShotFlavor {
  return {
    gain: f.gain * SUPPRESSED_GAIN,
    bandHz: f.bandHz * SUPPRESSED_BAND,
    decay: f.decay * SUPPRESSED_DECAY,
    // The thump is the bolt and the gas, and a can does not silence those —
    // dropping it as hard as the crack would leave a shot with no body at all.
    thumpHz: f.thumpHz * 0.92,
    thumpGain: f.thumpGain * SUPPRESSED_THUMP_GAIN,
    thumpSec: f.thumpSec * 0.85,
  };
}

/**
 * What a round sounds like arriving on a surface. Six materials, four voices
 * (the masonry three differ only in brightness). The point is telling brick
 * from sheet metal by sound alone.
 */
interface ImpactFlavor {
  /** Peak gain of the noise body at zero distance. */
  gain: number;
  /** Filter centre. With `lowpass` it is a cutoff; otherwise a bandpass pick. */
  hz: number;
  q: number;
  /** Noise decay, seconds. Short is what makes it read as a HIT. */
  seconds: number;
  /** A dull surface has no top end at all; a bandpass would give it one. */
  lowpass: boolean;
  /** Two partials rung on top — metal only. `null` for everything else. */
  ringHz: readonly [number, number] | null;
  ringGain: number;
  ringSeconds: number;
}

const NO_RING = { ringHz: null, ringGain: 0, ringSeconds: 0 } as const;

const IMPACT_FLAVORS: Record<MaterialId, ImpactFlavor> = {
  // A bright crack and gone: the sharpest and shortest of the masonry three.
  concrete: { gain: 0.5, hz: 3200, q: 1.6, seconds: 0.05, lowpass: false, ...NO_RING },
  brick: { gain: 0.45, hz: 2800, q: 1.4, seconds: 0.055, lowpass: false, ...NO_RING },
  stone: { gain: 0.48, hz: 2500, q: 1.5, seconds: 0.05, lowpass: false, ...NO_RING },
  // A knock, an octave and a half below the masonry crack and with enough tail
  // to sound hollow — which is also the sound of cover you can shoot through.
  wood: { gain: 0.42, hz: 900, q: 1.1, seconds: 0.085, lowpass: false, ...NO_RING },
  // No crack whatsoever. Earth absorbs; that is the whole sound.
  dirt: { gain: 0.38, hz: 500, q: 0.7, seconds: 0.09, lowpass: true, ...NO_RING },
  // A fallback only: rounds pass THROUGH glass and never stop on it, so this
  // is heard only if a pane ever becomes a wall. A short bright crack.
  glass: { gain: 0.35, hz: 4200, q: 2.2, seconds: 0.04, lowpass: false, ...NO_RING },
  // Quiet body, long ring: what makes it metal is the two detuned partials
  // still going a third of a second later.
  metal: {
    gain: 0.22,
    hz: 3000,
    q: 2,
    seconds: 0.04,
    lowpass: false,
    ringHz: [1700, 2300],
    ringGain: 0.1,
    ringSeconds: 0.45,
  },
};

/** How close somebody else's round must pass to be heard going by, metres. */
const WHIZ_RADIUS_M = 3;
const WHIZ_SECONDS = 0.08;
const WHIZ_START_HZ = 4000;
const WHIZ_END_HZ = 1500;
const WHIZ_GAIN = 0.25;

/** The wind bed: a lowpassed noise loop breathing on a very slow LFO. */
const WIND_CUTOFF_HZ = 350;
const WIND_LFO_HZ = 0.07;
/** Gain swings between MID − SWING and MID + SWING, i.e. 0.02 … 0.06. */
const WIND_GAIN_MID = 0.04;
const WIND_GAIN_SWING = 0.02;

/**
 * The rain bed: the same noise loop under a much higher lowpass (1.2 kHz
 * against the wind's 350 — a hiss rather than a roar). 0.05 sits between the
 * wind's floor and peak: audibly wetter, never loud enough to mask a footstep.
 */
const RAIN_CUTOFF_HZ = 1200;
const RAIN_GAIN = 0.05;

/** Distant birds, seconds between them. */
const BIRD_MIN_S = 6;
const BIRD_MAX_S = 15;
const BIRD_GAIN = 0.03;

/**
 * A flush fades to silence here — about as far as wings carry, and roughly the
 * range the birds are still a readable shape.
 */
const BIRD_FLUSH_RANGE_M = 80;

/** Wingbeats in one flush, and the seconds between them. */
const FLUSH_BEATS_MIN = 6;
const FLUSH_BEATS_MAX = 8;
const FLUSH_BEAT_GAP_S = 0.12;
/** One wingbeat: a short noise burst around this band. */
const FLUSH_BEAT_HZ = 900;
const FLUSH_BEAT_GAIN = 0.22;
/** The two alarm calls that go with it — a sharp descending sweep, twice. */
const FLUSH_CALL_HZ = 3900;
const FLUSH_CALL_GAIN = 0.12;

// --- Breaking glass ---------------------------------------------------------

/** A pane going fades to silence here — louder than an impact, quieter than a
 *  shot: a smashed window is a position, and it should carry. */
const GLASS_RANGE_M = 90;
/** The crack: high-passed noise, 80 ms. */
const GLASS_CRACK_HZ = 3000;
const GLASS_CRACK_SECONDS = 0.08;
const GLASS_CRACK_GAIN = 0.4;
/** ...and the shards landing after it: short sines, spread over this window. */
const GLASS_TINKLES_MIN = 3;
const GLASS_TINKLES_MAX = 4;
const GLASS_TINKLE_LOW_HZ = 2500;
const GLASS_TINKLE_HIGH_HZ = 6000;
const GLASS_TINKLE_SPREAD_S = 0.3;
const GLASS_TINKLE_GAIN = 0.09;

type Ctx = AudioContext;

function audioContextCtor(): (new () => AudioContext) | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    AudioContext?: new () => AudioContext;
    webkitAudioContext?: new () => AudioContext;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

export class GameAudio {
  private ctx: Ctx | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;

  /**
   * The gain the master node should carry — a WANT like `ambienceOn`, since the
   * volume is pushed at boot before any context exists.
   */
  private masterGain = MASTER_GAIN;

  /** `false` once we have established there is no audio in this environment. */
  private available = true;

  /** Distance accumulator for the footstep trigger, metres. */
  private stepAccum = 0;

  /** Previous `reloading` flag, so the rustle plays on the rising edge only. */
  private wasReloading = false;

  /**
   * The same latch for `checking` — its own field, since the two flags are
   * independent.
   */
  private wasChecking = false;

  /**
   * The ears, written every frame. Plain fields and one reused XZ object — the
   * frame path allocates nothing. Default: origin, yaw 0.
   */
  private readonly listenerXZ = { x: 0, z: 0 };
  private listenerY = 0;
  private listenerYaw = 0;
  /** Scratch for `panFor`'s source argument. Overwritten on every call. */
  private readonly srcXZ = { x: 0, z: 0 };

  /**
   * Ambience is a WANT: switched on when the raid screen opens, before a click
   * has allowed an AudioContext; `resume()` starts the bed.
   */
  private ambienceOn = false;
  private windSrc: AudioBufferSourceNode | null = null;
  private windLfo: OscillatorNode | null = null;
  /** Whether the raid is wet — a want like `ambienceOn`, set from `welcome`. */
  private wantRain = false;
  private rainSrc: AudioBufferSourceNode | null = null;
  /**
   * ...and whether it is white. Not a want — it only changes what a boot sounds
   * like.
   */
  private snow = false;
  /** Everything between the sources and the master, for teardown. */
  private ambienceChain: AudioNode[] = [];
  private birdTimer: ReturnType<typeof setTimeout> | null = null;

  /** Creates (or resumes) the context. Call from a real user gesture. */
  resume(): void {
    const ctx = this.ensureContext();
    if (ctx === null) return;
    if (ctx.state === "suspended") void ctx.resume();
    // The raid screen almost always asked for ambience before there was a
    // context to put it in; this is where that want is honoured.
    if (this.ambienceOn) this.startAmbience();
  }

  /**
   * Where the player is and faces. Call every frame; allocates nothing. Y is
   * kept only for distance — there is no elevation in a stereo mix.
   */
  setListener(pos: Vec3, yaw: number): void {
    // A NaN would poison every pan afterwards (and `StereoPannerNode` throws).
    if (!Number.isFinite(pos.x) || !Number.isFinite(pos.y) || !Number.isFinite(pos.z)) {
      return;
    }
    if (!Number.isFinite(yaw)) return;
    this.listenerXZ.x = pos.x;
    this.listenerXZ.z = pos.z;
    this.listenerY = pos.y;
    this.listenerYaw = yaw;
  }

  /**
   * The player's own shot: loud, close, a chest thump under it. Unknown
   * `weaponId` falls back to the carbine. Unpanned — it happens AT the player;
   * only the brass afterwards has a side.
   */
  localShot(weaponId = "", suppressed = false): void {
    const ctx = this.ensureContext();
    if (ctx === null) return;
    const base = SHOT_FLAVORS[weaponId] ?? CARBINE_SHOT;
    const f = suppressed ? suppress(base) : base;
    this.crack(ctx, f.gain, f.bandHz, f.decay);
    this.thump(ctx, f.thumpHz, f.thumpGain, f.thumpSec);
    // Not skipped when suppressed: a can does nothing to a case on the road.
    this.shellTink(ctx);
  }

  /**
   * Someone else's shot, attenuated and coloured by their weapon. Linear
   * falloff (1/r² makes anything past ~30 m inaudible). Never suppressed — the
   * mods map is not public, so everyone else's gun sounds bare. Takes the
   * muzzle POSITION and measures against the stored listener, so one place owns
   * a sound's geometry.
   */
  remoteShot(pos: Vec3, weaponId = ""): void {
    const ctx = this.ensureContext();
    if (ctx === null) return;
    const near = this.nearness(pos, SHOT_AUDIBLE_RANGE_M);
    if (near <= 0.01) return;
    const pan = this.panTo(pos);

    const f = SHOT_FLAVORS[weaponId] ?? CARBINE_SHOT;
    // Distant shots lose their top end — that is what reads as "far away".
    const FAR_BAND_HZ = 700;
    this.crack(
      ctx,
      f.gain * 0.6 * near,
      FAR_BAND_HZ + (f.bandHz - FAR_BAND_HZ) * near,
      f.decay * 1.25,
      pan,
    );
    // The thump stays centred: bass carries no direction, and panning it
    // lurches the mix.
    this.thump(ctx, f.thumpHz * 0.92, f.thumpGain * 0.55 * near, f.thumpSec);
  }

  /**
   * A round arriving on a surface. Played for the local player's own rounds too
   * — watching your bullets land is how you learn a gun.
   */
  impact(pos: Vec3, material: MaterialId): void {
    const ctx = this.ensureContext();
    if (ctx === null) return;
    const near = this.nearness(pos, IMPACT_AUDIBLE_RANGE_M);
    if (near <= 0.01) return;
    // Map data is downloaded JSON; an unknown material must not throw
    // mid-frame.
    const f = IMPACT_FLAVORS[material] ?? IMPACT_FLAVORS.concrete;
    this.spatter(ctx, f, near, this.panTo(pos));
  }

  /**
   * A grenade going off: a long low thump with noise on top. Distance makes it
   * quieter, duller and longer. The thump stays centred; the noise is panned.
   */
  explosion(pos: Vec3): void {
    const ctx = this.ensureContext();
    if (ctx === null) return;
    const near = this.nearness(pos, EXPLOSION_AUDIBLE_RANGE_M);
    if (near <= 0.01) return;

    this.thump(ctx, 74, 0.85 * near, 0.5 + 0.35 * (1 - near));
    const FAR_BAND_HZ = 220;
    this.crack(
      ctx,
      0.7 * near,
      FAR_BAND_HZ + (1400 - FAR_BAND_HZ) * near,
      1.8 + 1.4 * (1 - near),
      this.panTo(pos),
    );

    // Standing next to it. Not gated on whose grenade it was: the blast that
    // deafens you is at least as likely to be somebody else's.
    if (this.distanceTo(pos) <= EXPLOSION_RING_RANGE_M) this.flashRing();
  }

  /**
   * The whine left by a flashbang or a blast at arm's length: two detuned sines
   * beating into something that sounds like damage. Unpanned.
   */
  flashRing(): void {
    const ctx = this.ensureContext();
    if (ctx === null) return;
    this.blip(ctx, 3200, 1.8, 0.1, "sine");
    this.blip(ctx, 3320, 1.5, 0.06, "sine", 0.02);
  }

  /**
   * Birds going up off a spooked animal. POSITIONED, unlike the ambient
   * `chirp()`: this one IS the threat — a clatter off to your left is somebody
   * in that treeline. Two layers: six to eight jittered wingbeats and two
   * descending alarm calls.
   */
  birdFlush(pos: Vec3): void {
    const ctx = this.ensureContext();
    if (ctx === null) return;
    const near = this.nearness(pos, BIRD_FLUSH_RANGE_M);
    if (near <= 0.01) return;
    const pan = this.panTo(pos);

    const beats =
      FLUSH_BEATS_MIN +
      Math.floor(Math.random() * (FLUSH_BEATS_MAX - FLUSH_BEATS_MIN + 1));
    let at = 0;
    for (let i = 0; i < beats; i++) {
      // The first beat is the loudest: the flock leaves the branch together and
      // then spreads out, so the clatter decays over its own length.
      const decay = 1 - (i / beats) * 0.45;
      this.wingbeat(ctx, FLUSH_BEAT_GAIN * near * decay, at, pan);
      at += FLUSH_BEAT_GAP_S * (0.75 + Math.random() * 0.5);
    }

    // Both calls inside the first half of the clatter — an alarm is raised as
    // the birds go, not once they are gone.
    this.alarmCall(ctx, FLUSH_CALL_GAIN * near, 0.02, pan);
    this.alarmCall(ctx, FLUSH_CALL_GAIN * near * 0.8, 0.16 + Math.random() * 0.1, pan);
  }

  /**
   * A window pane going in. A bright noise crack and three or four shards
   * landing after it, pitched from a HASH of the position — every client hears
   * the same window break the same way, and two windows sound different.
   */
  shatter(pos: Vec3): void {
    const ctx = this.ensureContext();
    if (ctx === null) return;
    const near = this.nearness(pos, GLASS_RANGE_M);
    if (near <= 0.01) return;
    const pan = this.panTo(pos);

    // The crack itself: noise with everything below 3 kHz taken out.
    const src = this.noiseSource(ctx);
    if (src !== null) {
      const now = ctx.currentTime;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = GLASS_CRACK_HZ;
      hp.Q.value = 0.7;

      const env = ctx.createGain();
      env.gain.setValueAtTime(0, now);
      env.gain.linearRampToValueAtTime(GLASS_CRACK_GAIN * near, now + 0.002);
      env.gain.exponentialRampToValueAtTime(0.0008, now + GLASS_CRACK_SECONDS);

      src.connect(hp).connect(env);
      this.play(env, src, now, GLASS_CRACK_SECONDS + 0.02, pan);
    }

    // The tinkles. Deterministic per position, so the same pane sounds the
    // same on every client and two panes sound different.
    const seed = posHash(pos);
    const span = GLASS_TINKLES_MAX - GLASS_TINKLES_MIN + 1;
    const count = GLASS_TINKLES_MIN + (seed % span);
    for (let i = 0; i < count; i++) {
      const a = mix01(seed, i * 2 + 1);
      const b = mix01(seed, i * 2 + 2);
      const hz = GLASS_TINKLE_LOW_HZ + a * (GLASS_TINKLE_HIGH_HZ - GLASS_TINKLE_LOW_HZ);
      const seconds = 0.03 + b * 0.03;
      // Spread across the window, the first one right behind the crack.
      const delay = 0.03 + (i / count) * GLASS_TINKLE_SPREAD_S * (0.7 + b * 0.6);
      this.blip(ctx, hz, seconds, GLASS_TINKLE_GAIN * near, "sine", delay, pan);
    }
  }

  /**
   * Somebody else's round going past your head. Takes the SEGMENT the round
   * flew this frame — at 700 m/s that is eleven metres, so sampled points
   * would miss most near misses. Returns whether it played, so the caller fires
   * it at most once per round.
   */
  whiz(from: Vec3, to: Vec3): boolean {
    const ctx = this.ensureContext();
    if (ctx === null) return false;

    const ax = from.x;
    const ay = from.y;
    const az = from.z;
    const ex = to.x - ax;
    const ey = to.y - ay;
    const ez = to.z - az;
    const lenSq = ex * ex + ey * ey + ez * ez;

    const lx = this.listenerXZ.x;
    const ly = this.listenerY;
    const lz = this.listenerXZ.z;

    // Projection of the listener onto the segment, clamped to its ends: a round
    // that stopped short of you never whizzes past you.
    let t = lenSq > 0 ? ((lx - ax) * ex + (ly - ay) * ey + (lz - az) * ez) / lenSq : 0;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const cx = ax + ex * t;
    const cy = ay + ey * t;
    const cz = az + ez * t;

    const dx = cx - lx;
    const dy = cy - ly;
    const dz = cz - lz;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    // Written as `!(… < …)` so a NaN anywhere upstream is a miss, not a snap.
    if (!(dist < WHIZ_RADIUS_M)) return false;

    // Halves over the radius rather than fading to nothing: a round three
    // metres off is still a round three metres off, and it should be audible.
    const gain = WHIZ_GAIN * (1 - 0.5 * (dist / WHIZ_RADIUS_M));
    this.srcXZ.x = cx;
    this.srcXZ.z = cz;
    this.snap(ctx, gain, panFor(this.listenerXZ, this.listenerYaw, this.srcXZ));
    return true;
  }

  /**
   * The master volume on the settings' 0..100 scale (50 = unity). Pushed in,
   * never pulled — this class imports nothing of ours. Safe before a context,
   * every frame, and with rubbish (a NaN in a `GainNode` silences the game).
   */
  setMasterVolume(volume: number): void {
    const gain = masterGainFor(volume);
    if (gain === this.masterGain) return;
    this.masterGain = gain;
    if (this.master !== null) this.master.gain.value = gain;
  }

  /**
   * The wind-and-birds bed. Subtle by contract (footstep 0.16, loudest thing
   * here 0.06): ambience must never mask somebody walking up behind you. Safe
   * to repeat and safe before a context.
   */
  setAmbience(on: boolean): void {
    if (on === this.ambienceOn) return;
    this.ambienceOn = on;
    if (!on) {
      this.stopAmbience();
      return;
    }
    // No `ensureContext`: entering the raid is not a user gesture.
    if (this.ctx !== null) this.startAmbience();
  }

  /**
   * Time of day and weather, from `welcome` (and `DEFAULT_ENV` when the raid
   * ends). Only rain is audible — fog and snow are silent; snow changes what is
   * underfoot instead (`step`). Rain gets a bed restarted through the ambience.
   */
  setEnv(env: RaidEnv): void {
    this.snow = env.weather === "snow";
    const wet = env.weather === "rain";
    if (wet === this.wantRain) return;
    this.wantRain = wet;
    // A playing bed is rebuilt to gain or lose its rain layer; otherwise the
    // want is remembered for `startAmbience`.
    if (this.ambienceOn && this.windSrc !== null) {
      this.stopAmbience();
      this.startAmbience();
    }
  }

  /** Hit confirmation: a short, bright tick. Deliberately not musical. */
  hitConfirm(): void {
    const ctx = this.ensureContext();
    if (ctx === null) return;
    this.blip(ctx, 1400, 0.05, 0.22, "square");
  }

  /** A kill, or your own death: two falling tones. */
  deathSting(): void {
    const ctx = this.ensureContext();
    if (ctx === null) return;
    this.blip(ctx, 340, 0.22, 0.3, "sine");
    this.blip(ctx, 190, 0.5, 0.26, "sine", 0.12);
  }

  /**
   * Drives the reload rustle from `YouSnap.weapon.reloading`; plays on the
   * false → true edge.
   */
  setReloading(reloading: boolean): void {
    if (reloading === this.wasReloading) return;
    this.wasReloading = reloading;
    if (!reloading) return;
    const ctx = this.ensureContext();
    if (ctx === null) return;
    // Magazine out, magazine in: two soft noise scrapes a beat apart.
    this.scrape(ctx, 0.18, 0);
    this.scrape(ctx, 0.22, 0.55);
  }

  /**
   * Drives the magazine check off `YouSnap.weapon.checking`, on the rising
   * edge like `setReloading`. The reload's gesture, quieter, with the second
   * scrape softer than the first — a box slipping back, not a magazine seated.
   * Nothing on the falling edge: a cancelling shot drowns it anyway.
   */
  setMagChecking(checking: boolean): void {
    if (checking === this.wasChecking) return;
    this.wasChecking = checking;
    if (!checking) return;
    const ctx = this.ensureContext();
    if (ctx === null) return;
    this.scrape(ctx, 0.14, 0);
    this.scrape(ctx, 0.12, 0.5);
  }

  /**
   * Accumulates distance and plays a footstep per stride; a jump banks nothing.
   * Snow crunches (nearly two octaves up) where mud thuds.
   */
  step(movedM: number, onGround: boolean, crouched: boolean): void {
    if (!onGround || !(movedM > 0)) return;
    // A teleport must not fire a burst of footsteps.
    if (movedM > 2) return;
    this.stepAccum += movedM;
    if (this.stepAccum < STEP_DISTANCE_M) return;
    this.stepAccum = 0;

    const ctx = this.ensureContext();
    if (ctx === null) return;
    // Crouched is quieter AND duller — the reason to crouch when someone might
    // listen.
    if (this.snow) this.footstep(ctx, crouched ? 0.06 : 0.15, crouched ? 1400 : 2600);
    else this.footstep(ctx, crouched ? 0.055 : 0.16, crouched ? 700 : 1500);
  }

  dispose(): void {
    // Before the context goes: the ambience owns a looping source and a
    // `setTimeout` chain that would otherwise keep re-arming itself forever.
    this.ambienceOn = false;
    this.stopAmbience();
    const ctx = this.ctx;
    this.ctx = null;
    this.master = null;
    this.noise = null;
    this.available = false;
    if (ctx !== null) void ctx.close();
  }

  // -----------------------------------------------------------------------
  // Placement
  // -----------------------------------------------------------------------

  /**
   * 1 at the listener's feet, 0 at `rangeM` and beyond; 0 for a non-finite
   * position, so garbage is silent.
   */
  private nearness(pos: Vec3, rangeM: number): number {
    const dx = pos.x - this.listenerXZ.x;
    const dy = pos.y - this.listenerY;
    const dz = pos.z - this.listenerXZ.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (!Number.isFinite(d)) return 0;
    return 1 - Math.min(d / rangeM, 1);
  }

  /** Metres from the listener to a world position. `Infinity` for garbage, so
   *  every comparison against it fails closed. */
  private distanceTo(pos: Vec3): number {
    const dx = pos.x - this.listenerXZ.x;
    const dy = pos.y - this.listenerY;
    const dz = pos.z - this.listenerXZ.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    return Number.isFinite(d) ? d : Infinity;
  }

  /** Stereo pan for a world position, through the shared scratch object. */
  private panTo(pos: Vec3): number {
    this.srcXZ.x = pos.x;
    this.srcXZ.z = pos.z;
    return panFor(this.listenerXZ, this.listenerYaw, this.srcXZ);
  }

  /**
   * A fresh `StereoPannerNode` into the master bus, or the bus itself. Per
   * sound: a gunshot's pan is fixed the moment it fires. `createStereoPanner`
   * is feature-detected — a missing feature costs sound, never a crash.
   */
  private busFor(pan: number | null): AudioNode | null {
    const ctx = this.ctx;
    const master = this.master;
    if (ctx === null || master === null) return null;
    if (pan === null || pan === 0 || !Number.isFinite(pan)) return master;
    if (typeof ctx.createStereoPanner !== "function") return master;
    let panner: StereoPannerNode;
    try {
      panner = ctx.createStereoPanner();
    } catch {
      return master;
    }
    panner.pan.value = pan;
    panner.connect(master);
    return panner;
  }

  /** Tears down whatever `busFor` built. The master bus outlives every sound
   *  on it and must never be disconnected here. */
  private dropBus(bus: AudioNode): void {
    // `master === null` means `dispose` has already been through; the whole
    // graph is going with the context and nothing here should touch it.
    if (this.master === null || bus === this.master) return;
    bus.disconnect();
  }

  // -----------------------------------------------------------------------
  // Synthesis primitives
  // -----------------------------------------------------------------------

  private ensureContext(): Ctx | null {
    if (!this.available) return null;
    if (this.ctx !== null) return this.ctx;

    const Ctor = audioContextCtor();
    if (Ctor === null) {
      this.available = false;
      return null;
    }
    let ctx: AudioContext;
    try {
      ctx = new Ctor();
    } catch {
      // Some environments expose the constructor but refuse to build one.
      this.available = false;
      return null;
    }

    const master = ctx.createGain();
    master.gain.value = this.masterGain;
    master.connect(ctx.destination);

    this.ctx = ctx;
    this.master = master;
    this.noise = makeNoiseBuffer(ctx);
    return ctx;
  }

  /**
   * The body of a gunshot: noise through a bandpass (the crack) and a closing
   * lowpass (the blast). `decayScale` stretches the tail for distance.
   */
  private crack(
    ctx: Ctx,
    gain: number,
    bandHz: number,
    decayScale: number,
    pan: number | null = null,
  ): void {
    const src = this.noiseSource(ctx);
    if (src === null) return;

    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.frequency.value = bandHz;
    band.Q.value = 0.7;

    const low = ctx.createBiquadFilter();
    low.type = "lowpass";
    const now = ctx.currentTime;
    low.frequency.setValueAtTime(9000, now);
    low.frequency.exponentialRampToValueAtTime(600, now + 0.09 * decayScale);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, now);
    // 3 ms attack: any slower and the shot loses its snap entirely.
    env.gain.linearRampToValueAtTime(gain, now + 0.003);
    env.gain.exponentialRampToValueAtTime(0.0008, now + 0.16 * decayScale);

    src.connect(band).connect(low).connect(env);
    this.play(env, src, now, 0.2 * decayScale, pan);
  }

  /** The low-frequency punch under a gunshot. */
  private thump(
    ctx: Ctx,
    hz: number,
    gain: number,
    seconds: number,
    pan: number | null = null,
  ): void {
    const bus = this.busFor(pan);
    if (bus === null) return;
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(hz, now);
    osc.frequency.exponentialRampToValueAtTime(hz * 0.45, now + seconds);

    const env = ctx.createGain();
    env.gain.setValueAtTime(gain, now);
    env.gain.exponentialRampToValueAtTime(0.0008, now + seconds);

    osc.connect(env).connect(bus);
    osc.start(now);
    osc.stop(now + seconds + 0.02);
    osc.onended = () => {
      osc.disconnect();
      env.disconnect();
      this.dropBus(bus);
    };
  }

  private blip(
    ctx: Ctx,
    hz: number,
    seconds: number,
    gain: number,
    type: OscillatorType,
    delay = 0,
    pan: number | null = null,
  ): void {
    const bus = this.busFor(pan);
    if (bus === null) return;
    const start = ctx.currentTime + delay;

    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(hz, start);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, start);
    env.gain.linearRampToValueAtTime(gain, start + 0.004);
    env.gain.exponentialRampToValueAtTime(0.0008, start + seconds);

    osc.connect(env).connect(bus);
    osc.start(start);
    osc.stop(start + seconds + 0.02);
    osc.onended = () => {
      osc.disconnect();
      env.disconnect();
      this.dropBus(bus);
    };
  }

  /** Cloth-and-metal rustle: noise through a mid bandpass, slow attack. */
  private scrape(ctx: Ctx, gain: number, delay: number): void {
    const src = this.noiseSource(ctx);
    if (src === null) return;
    const start = ctx.currentTime + delay;

    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.frequency.value = 2200;
    band.Q.value = 1.4;

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, start);
    env.gain.linearRampToValueAtTime(gain, start + 0.04);
    env.gain.exponentialRampToValueAtTime(0.0008, start + 0.22);

    src.connect(band).connect(env);
    this.play(env, src, start, 0.3);
  }

  /** A footstep: a very short, low noise thud. */
  private footstep(ctx: Ctx, gain: number, cutoffHz: number): void {
    const src = this.noiseSource(ctx);
    if (src === null) return;
    const now = ctx.currentTime;

    const low = ctx.createBiquadFilter();
    low.type = "lowpass";
    low.frequency.value = cutoffHz;

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(gain, now + 0.006);
    env.gain.exponentialRampToValueAtTime(0.0008, now + 0.075);

    src.connect(low).connect(env);
    this.play(env, src, now, 0.12);
  }

  /**
   * A bullet arriving: a filtered noise burst plus a ring for metal. `near`
   * scales gain only — a distant impact keeps its colour.
   */
  private spatter(ctx: Ctx, f: ImpactFlavor, near: number, pan: number): void {
    const src = this.noiseSource(ctx);
    if (src === null) return;
    const now = ctx.currentTime;

    const filter = ctx.createBiquadFilter();
    filter.type = f.lowpass ? "lowpass" : "bandpass";
    filter.frequency.value = f.hz;
    filter.Q.value = f.q;

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, now);
    // 2 ms: an impact is an attack with a bit of decay stuck to it, and any
    // slower turns the crack into a "wub".
    env.gain.linearRampToValueAtTime(f.gain * near, now + 0.002);
    env.gain.exponentialRampToValueAtTime(0.0008, now + f.seconds);

    src.connect(filter).connect(env);
    this.play(env, src, now, f.seconds + 0.02, pan);

    if (f.ringHz === null) return;
    // Detuned per hit: a fixed pair of partials turns bursts into a melody.
    for (const hz of f.ringHz) {
      const detune = 0.96 + Math.random() * 0.08;
      this.blip(ctx, hz * detune, f.ringSeconds, f.ringGain * near, "triangle", 0, pan);
    }
  }

  /** A round going past: a bright noise snap whose band falls as it passes. */
  private snap(ctx: Ctx, gain: number, pan: number): void {
    const src = this.noiseSource(ctx);
    if (src === null) return;
    const now = ctx.currentTime;

    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.Q.value = 3;
    // The falling band IS the Doppler — a downward sweep reads as "that went
    // past me".
    band.frequency.setValueAtTime(WHIZ_START_HZ, now);
    band.frequency.exponentialRampToValueAtTime(WHIZ_END_HZ, now + WHIZ_SECONDS);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(gain, now + 0.008);
    env.gain.exponentialRampToValueAtTime(0.0008, now + WHIZ_SECONDS);

    src.connect(band).connect(env);
    this.play(env, src, now, WHIZ_SECONDS + 0.02, pan);
  }

  /**
   * One wingbeat: 40 ms of band-limited noise with a 4 ms attack (a slap, not a
   * crack), detuned per beat.
   */
  private wingbeat(ctx: Ctx, gain: number, delay: number, pan: number): void {
    const src = this.noiseSource(ctx);
    if (src === null) return;
    const start = ctx.currentTime + delay;
    const seconds = 0.03 + Math.random() * 0.02;

    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.frequency.value = FLUSH_BEAT_HZ * (0.85 + Math.random() * 0.3);
    band.Q.value = 1.1;

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, start);
    env.gain.linearRampToValueAtTime(gain, start + 0.004);
    env.gain.exponentialRampToValueAtTime(0.0008, start + seconds);

    src.connect(band).connect(env);
    this.play(env, src, start, seconds + 0.02, pan);
  }

  /**
   * A bird's alarm call: a short FM sweep down from ~4 kHz — the sweep is what
   * reads as a creature. Panned with the beats.
   */
  private alarmCall(ctx: Ctx, gain: number, delay: number, pan: number): void {
    const bus = this.busFor(pan);
    if (bus === null) return;
    const start = ctx.currentTime + delay;
    const seconds = 0.09;
    const top = FLUSH_CALL_HZ * (0.9 + Math.random() * 0.2);

    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(top, start);
    osc.frequency.exponentialRampToValueAtTime(top * 0.62, start + seconds);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, start);
    env.gain.linearRampToValueAtTime(gain, start + 0.006);
    env.gain.exponentialRampToValueAtTime(0.0008, start + seconds);

    osc.connect(env).connect(bus);
    osc.start(start);
    osc.stop(start + seconds + 0.02);
    osc.onended = () => {
      osc.disconnect();
      env.disconnect();
      this.dropBus(bus);
    };
  }

  /**
   * The player's own case hitting the ground a beat after the shot, scheduled
   * on the audio clock. Panned slightly right.
   */
  private shellTink(ctx: Ctx): void {
    const delay = 0.25 + Math.random() * 0.2;
    const hz = 3500 + Math.random() * 1500;
    this.blip(ctx, hz, 0.035, 0.07, "triangle", delay, 0.3);
    // The bounce: quieter, a touch off-pitch, and close behind the first.
    this.blip(
      ctx,
      hz * (0.8 + Math.random() * 0.3),
      0.03,
      0.05,
      "sine",
      delay + 0.03 + Math.random() * 0.05,
      0.3,
    );
  }

  // -----------------------------------------------------------------------
  // Ambience
  // -----------------------------------------------------------------------

  /** Idempotent: called from `resume()` on every pointer-lock click. */
  private startAmbience(): void {
    const ctx = this.ctx;
    const master = this.master;
    const buf = this.noise;
    if (ctx === null || master === null || buf === null) return;
    if (this.windSrc !== null) return;

    const now = ctx.currentTime;

    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    // Half speed: a one-second loop an octave down, inaudible as a loop under
    // a 350 Hz lowpass at 0.06.
    src.playbackRate.value = 0.5;

    const low = ctx.createBiquadFilter();
    low.type = "lowpass";
    low.frequency.value = WIND_CUTOFF_HZ;

    const gain = ctx.createGain();
    gain.gain.value = WIND_GAIN_MID;

    // A 14-second breath — the bed not being perfectly static.
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = WIND_LFO_HZ;
    const depth = ctx.createGain();
    depth.gain.value = WIND_GAIN_SWING;
    lfo.connect(depth);
    depth.connect(gain.gain);

    src.connect(low).connect(gain).connect(master);
    src.start(now);
    lfo.start(now);

    this.windSrc = src;
    this.windLfo = lfo;
    this.ambienceChain = [low, gain, depth];

    // Rain rides on top of the wind; started here so `startAmbience`/
    // `stopAmbience` own the lifetime.
    if (this.wantRain) {
      const rain = ctx.createBufferSource();
      rain.buffer = buf;
      rain.loop = true;
      // Full rate, unlike the wind's half — also what stops the two beds
      // phasing.
      const rainLow = ctx.createBiquadFilter();
      rainLow.type = "lowpass";
      rainLow.frequency.value = RAIN_CUTOFF_HZ;
      const rainGain = ctx.createGain();
      rainGain.gain.value = RAIN_GAIN;
      rain.connect(rainLow).connect(rainGain).connect(master);
      rain.start(now);
      this.rainSrc = rain;
      this.ambienceChain.push(rainLow, rainGain);
    }

    this.scheduleBird();
  }

  private stopAmbience(): void {
    if (this.birdTimer !== null) {
      clearTimeout(this.birdTimer);
      this.birdTimer = null;
    }

    const src = this.windSrc;
    const lfo = this.windLfo;
    const rain = this.rainSrc;
    this.windSrc = null;
    this.windLfo = null;
    this.rainSrc = null;

    // A stop on an already-stopped node, or on a node whose context is going
    // away underneath it, is not worth taking the caller down for.
    try {
      if (src !== null) {
        src.stop();
        src.disconnect();
      }
      if (lfo !== null) {
        lfo.stop();
        lfo.disconnect();
      }
      if (rain !== null) {
        rain.stop();
        rain.disconnect();
      }
      for (const node of this.ambienceChain) node.disconnect();
    } catch {
      // Nothing to do: the nodes are already unreachable either way.
    }
    this.ambienceChain = [];
  }

  /**
   * Arms the next bird and re-arms itself. A `setTimeout`, since the interval
   * is seconds long and must be cancellable.
   */
  private scheduleBird(): void {
    const ms = (BIRD_MIN_S + Math.random() * (BIRD_MAX_S - BIRD_MIN_S)) * 1000;
    this.birdTimer = setTimeout(() => {
      this.birdTimer = null;
      if (!this.ambienceOn) return;
      const ctx = this.ctx;
      if (ctx !== null) this.chirp(ctx);
      this.scheduleBird();
    }, ms);
  }

  /**
   * Two or three quick notes in the trees. Unpanned and quiet: a bird is
   * scenery, and a placeable one would make players turn toward nothing. The
   * opposite of `birdFlush`.
   */
  private chirp(ctx: Ctx): void {
    const notes = Math.random() < 0.5 ? 2 : 3;
    const base = 2800 + Math.random() * 1200;
    let at = 0;
    for (let i = 0; i < notes; i++) {
      this.blip(ctx, base * (0.94 + Math.random() * 0.12), 0.05, BIRD_GAIN, "sine", at);
      at += 0.07 + Math.random() * 0.05;
    }
  }

  /** A one-shot source reading a random slice of the shared noise buffer. */
  private noiseSource(ctx: Ctx): AudioBufferSourceNode | null {
    const buf = this.noise;
    if (buf === null) return null;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    // Random offset and detune, so two consecutive shots are never identical —
    // the single most obvious tell of synthesised audio.
    src.playbackRate.value = 0.92 + Math.random() * 0.16;
    return src;
  }

  /** Connects a finished chain to the master bus and schedules its cleanup. */
  private play(
    tail: AudioNode,
    src: AudioBufferSourceNode,
    startAt: number,
    seconds: number,
    pan: number | null = null,
  ): void {
    const bus = this.busFor(pan);
    const buf = this.noise;
    if (bus === null || buf === null) return;
    tail.connect(bus);
    // Start from a random point in the buffer for the same anti-repetition
    // reason as the detune above.
    const offset = Math.random() * Math.max(buf.duration - seconds, 0);
    src.start(startAt, offset, seconds);
    src.onended = () => {
      src.disconnect();
      tail.disconnect();
      this.dropBus(bus);
    };
  }
}

/**
 * A 32-bit mix, the renderer's `hash32` reimplemented here — `synth.ts`
 * deliberately imports nothing of ours, and pulling in `render/palette.ts`
 * would drag three into the audio module.
 */
function mix32(a: number, b: number): number {
  let h = Math.imul(a ^ 0x9e3779b9, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h ^ Math.imul(b + 0x165667b1, 0x27d4eb2f), 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** Deterministic pseudo-random in [0, 1) from a seed and a stream index. */
function mix01(seed: number, i: number): number {
  return mix32(seed, i) / 4294967296;
}

/** A world position quantised to a centimetre and mixed down to one integer. */
function posHash(pos: Vec3): number {
  const x = Math.round(pos.x * 100) | 0;
  const y = Math.round(pos.y * 100) | 0;
  const z = Math.round(pos.z * 100) | 0;
  return mix32(mix32(x, y), z);
}

function makeNoiseBuffer(ctx: Ctx): AudioBuffer | null {
  try {
    const frames = Math.floor(ctx.sampleRate * NOISE_SECONDS);
    const buf = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
    return buf;
  } catch {
    return null;
  }
}
